import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../config/constants";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import { FaExclamationTriangle, FaLock, FaRedo, FaTimes, FaTrash } from "react-icons/fa";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
  danger: "#dc2626",
};

const normalizeRole = (profile) =>
  String(profile?.role || profile?.staffrole || "").trim().toLowerCase();

const ResetAccountPage = () => {
  const [userData, setUserData] = useState({});
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [pendingLabel, setPendingLabel] = useState("");
  const [pendingScopes, setPendingScopes] = useState([]);
  const [loading, setLoading] = useState(false);

  const isOwner = useMemo(() => normalizeRole(userData) === "owner", [userData]);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("userData") || "{}");
    const profile = stored?.data || {};
    setUserData(profile);
  }, []);

  const actions = useMemo(
    () => [
      {
        key: "orders",
        title: "Orders",
        subtitle: "Clear all orders, KOT, and QR orders.",
        icon: <FaTrash />,
      },
      {
        key: "products",
        title: "Products",
        subtitle: "Disable all products, variants, and add-ons.",
        icon: <FaTrash />,
      },
      {
        key: "categories",
        title: "Categories",
        subtitle: "Clear product categories after products are reset.",
        icon: <FaTrash />,
      },
      {
        key: "inventory",
        title: "Inventory",
        subtitle: "Delete all inventory items, stock in/out, and closures.",
        icon: <FaTrash />,
      },
      {
        key: "expense",
        title: "Expenses",
        subtitle: "Delete all expense entries and categories.",
        icon: <FaTrash />,
      },
      {
        key: "income",
        title: "Income",
        subtitle: "Delete all income entries and categories.",
        icon: <FaTrash />,
      },
    ],
    []
  );

  const openConfirm = (label, scopes) => {
    setConfirmText("");
    setPendingLabel(label);
    setPendingScopes(scopes);
    setConfirmVisible(true);
  };

  const confirmMatches = confirmText.trim().toUpperCase() === "RESET";

  const resetRequest = async () => {
    if (!isOwner || !userData?.id) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/account/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: userData.id,
          confirm: confirmText.trim(),
          scopes: pendingScopes,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        const message = json?.message || json?.errors?.message || "Reset failed. Please try again.";
        if (res.status === 409) {
          toast.error(message || "Reset blocked. Reset products before categories.");
          return;
        }
        throw new Error(message);
      }
      toast.success("Reset completed successfully.");
      setConfirmVisible(false);
    } catch (error) {
      toast.error(error?.message || "Reset failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2500} />
      <Header>
        <Title>Reset Account</Title>
        <Sub>Permanently clear selected business data for this restaurant.</Sub>
      </Header>

      {!isOwner ? (
        <OwnerOnly>
          <OwnerCard>
            <FaExclamationTriangle />
            <div>
              <OwnerTitle>Danger Zone</OwnerTitle>
              <OwnerSub>Only owners can reset the account.</OwnerSub>
            </div>
          </OwnerCard>
          <OwnerLock>
            <FaLock />
            <span>Access restricted to owners.</span>
          </OwnerLock>
        </OwnerOnly>
      ) : (
        <Content>
          <Hero>
            <Eyebrow>Danger Zone</Eyebrow>
            <HeroTitle>Reset Account</HeroTitle>
            <HeroSub>This action is irreversible. Please make sure you have a backup.</HeroSub>
          </Hero>

          <Warning>
            <WarnIcon>
              <FaExclamationTriangle />
            </WarnIcon>
            <div>
              <WarnTitle>Danger Zone</WarnTitle>
              <WarnBody>This action is irreversible. Please make sure you have a backup.</WarnBody>
            </div>
          </Warning>

          <MasterCard>
            <MasterHeader>
              <MasterIcon>
                <FaRedo />
              </MasterIcon>
              <MasterTitle>Master Reset</MasterTitle>
            </MasterHeader>
            <MasterSub>
              Deletes orders, products, categories, inventory, expenses, and income data.
            </MasterSub>
            <MasterBtn
              onClick={() =>
                openConfirm("Master Reset", [
                  "orders",
                  "products",
                  "categories",
                  "inventory",
                  "expense",
                  "income",
                ])
              }
            >
              <FaTrash /> Master Reset
            </MasterBtn>
          </MasterCard>

          <SectionHeader>
            <SectionTitle>Reset Individual Data</SectionTitle>
            <Divider />
          </SectionHeader>

          <Grid>
            {actions.map((action) => (
              <ActionCard key={action.key}>
                <ActionIcon>{action.icon}</ActionIcon>
                <ActionTitle>{action.title}</ActionTitle>
                <ActionSub>{action.subtitle}</ActionSub>
                <ActionBtn onClick={() => openConfirm(action.title, [action.key])}>
                  Reset
                </ActionBtn>
              </ActionCard>
            ))}
          </Grid>
        </Content>
      )}

      {confirmVisible && (
        <ModalOverlay onClick={() => setConfirmVisible(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalIcon>
                <FaExclamationTriangle />
              </ModalIcon>
              <ModalTitle>Confirm Reset</ModalTitle>
              <IconBtn onClick={() => setConfirmVisible(false)}>
                <FaTimes />
              </IconBtn>
            </ModalHeader>
            <ModalSub>Type RESET to confirm {pendingLabel} reset. This cannot be undone.</ModalSub>
            <ModalInput
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type RESET"
              autoCapitalize="characters"
            />
            <ModalRow>
              <ModalGhost onClick={() => setConfirmVisible(false)} disabled={loading}>
                Cancel
              </ModalGhost>
              <ModalDanger
                onClick={resetRequest}
                disabled={!confirmMatches || loading}
              >
                {loading ? "Resetting..." : "Reset"}
              </ModalDanger>
            </ModalRow>
          </ModalCard>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default ResetAccountPage;

const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
`;
const Header = styled.div`
  margin-bottom: 16px;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: ${COLOR.text};
`;
const Sub = styled.p`
  margin: 6px 0 0;
  color: ${COLOR.muted};
`;
const Content = styled.div``;
const Hero = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 16px;
`;
const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #94a3b8;
`;
const HeroTitle = styled.h2`
  margin: 6px 0 0;
  font-size: 18px;
  font-weight: 800;
  color: ${COLOR.text};
`;
const HeroSub = styled.p`
  margin: 6px 0 0;
  color: ${COLOR.muted};
  font-size: 13px;
`;
const Warning = styled.div`
  margin-top: 14px;
  display: flex;
  gap: 12px;
  padding: 14px;
  border-radius: 14px;
  background: #fffbeb;
  border: 1px solid #fcd34d;
`;
const WarnIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  background: #fef3c7;
  color: #b45309;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const WarnTitle = styled.div`
  font-weight: 800;
  color: #92400e;
`;
const WarnBody = styled.div`
  color: #92400e;
  font-size: 12px;
  margin-top: 4px;
`;
const MasterCard = styled.div`
  margin-top: 16px;
  padding: 16px;
  border-radius: 16px;
  background: #111827;
  color: #fff;
`;
const MasterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const MasterIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: ${COLOR.danger};
  display: flex;
  align-items: center;
  justify-content: center;
`;
const MasterTitle = styled.div`
  font-size: 16px;
  font-weight: 800;
`;
const MasterSub = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #cbd5f5;
`;
const MasterBtn = styled.button`
  margin-top: 12px;
  background: ${COLOR.danger};
  color: #fff;
  border: none;
  padding: 10px 12px;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 800;
`;
const SectionHeader = styled.div`
  margin-top: 18px;
  display: flex;
  align-items: center;
  gap: 10px;
`;
const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;
const Divider = styled.div`
  flex: 1;
  height: 1px;
  background: ${COLOR.line};
`;
const Grid = styled.div`
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
`;
const ActionCard = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
`;
const ActionIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef2ff;
  color: ${COLOR.primary};
`;
const ActionTitle = styled.div`
  margin-top: 8px;
  font-size: 14px;
  font-weight: 800;
  color: ${COLOR.text};
`;
const ActionSub = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${COLOR.muted};
`;
const ActionBtn = styled.button`
  margin-top: 10px;
  background: ${COLOR.primary};
  color: #fff;
  border: none;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 700;
  font-size: 12px;
`;
const OwnerOnly = styled.div`
  display: grid;
  gap: 12px;
`;
const OwnerCard = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  padding: 16px;
  border-radius: 14px;
`;
const OwnerTitle = styled.div`
  font-weight: 800;
  color: ${COLOR.text};
`;
const OwnerSub = styled.div`
  color: ${COLOR.muted};
  font-size: 12px;
  margin-top: 4px;
`;
const OwnerLock = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px;
  border-radius: 14px;
  border: 1px solid #fca5a5;
  background: #fef2f2;
  color: #b91c1c;
  font-weight: 700;
`;
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 999;
`;
const ModalCard = styled.div`
  background: #fff;
  border-radius: 16px;
  max-width: 460px;
  width: 100%;
  padding: 16px;
`;
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const ModalIcon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 15px;
  background: ${COLOR.danger};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: ${COLOR.text};
  flex: 1;
`;
const IconBtn = styled.button`
  border: none;
  background: transparent;
  color: ${COLOR.muted};
  cursor: pointer;
`;
const ModalSub = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: ${COLOR.muted};
`;
const ModalInput = styled.input`
  margin-top: 12px;
  width: 100%;
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 14px;
  color: ${COLOR.text};
  background: #f8fafc;
`;
const ModalRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 14px;
`;
const ModalGhost = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 12px;
  border: none;
  background: #f1f5f9;
  color: ${COLOR.text};
  font-weight: 700;
  cursor: pointer;
`;
const ModalDanger = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 12px;
  border: none;
  background: ${COLOR.danger};
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
`;
